import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import * as React from 'react'
import { RegisterStudentRequest } from '../../../services/api-requests/api-request'
import { withRouter } from 'next/router'
import { WithRouterProps } from 'next/dist/client/with-router'
import { redirectOnAuthSuccess } from '../../../services/utils'
import { AnalyticService } from '../../../lib/analytics/analyticService'
import { Error, Visibility, VisibilityOff } from '@mui/icons-material'
import { useSnackbarComponent } from '../../SnackbarComponents/useSnackbarComponent'
import AuthExtrafieldsDialogComponent from './AuthExtrafieldsDialogComponent'
import { trackFbPixelsRegistrationToSchoolDetails } from '../../../lib/analytics/fbPixels'
import useSchoolUserAuth from './useSchoolUserAuth'

function validateEmail(email: string) {
  email = email.trim()
  // check if email
  const re = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
  return re.test(email)
}

interface IRegisterComponentProps extends WithRouterProps {
  school_details: any
  isDialog: boolean
  onSuccess: () => void
  setErrorMessage: (errMessage: string) => void
  prefill?: { email?: string; userData?: any }
}

const RegisterStudentComponent = (props: IRegisterComponentProps) => {
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [customAuthInput] = React.useState<any[] | undefined>(
    props.school_details?.custom_auth_input,
  )
  const [redirectUrl, setRedirectUrl] = React.useState('')
  const [userCustomInputAnswer, setUserCustomInputAnswer] = React.useState<any>(
    {},
  )
  const [registerSubmitLoading, setRegisterSubmitLoading] = React.useState(
    false,
  )
  const [showPassword, setShowPassword] = React.useState(false)
  const [passwordNotSame, setPasswordNotSame] = React.useState(false)
  const [registerWith, setRegisterWith] = React.useState('')
  const snackbarHook = useSnackbarComponent()
  const schoolUserAuthHook = useSchoolUserAuth()

  React.useEffect(() => {
    console.log('register props', props)
    setRedirectUrl(
      props.router && props.router.query.redirect
        ? (props.router.query.redirect as string)
        : '',
    )
  }, [])

  React.useEffect(() => {
    props.prefill?.email && setEmail(props.prefill.email)
    if (props.prefill?.userData) {
      setRegisterWith(props.prefill.userData.login_with)
    }
  }, [props.prefill?.email, props.prefill?.userData])

  React.useEffect(() => {
    if (!password || !confirmPassword) {
      setPasswordNotSame(false)
      return
    }

    setPasswordNotSame(password !== confirmPassword)
  }, [password, confirmPassword])

  const aCustomAuthFieldsIsEmpty = () => {
    let result = false
    customAuthInput?.map((item: any) => {
      const custom_inputs = userCustomInputAnswer?.[item?.fieldName]
      if (!custom_inputs?.trim?.() && item.isRequired) {
        result = true
      }
    })

    return result
  }

  const shouldEnableSubmit = () => {
    if (
      !firstName ||
      !lastName ||
      !email ||
      !password ||
      !confirmPassword ||
      aCustomAuthFieldsIsEmpty()
    ) {
      props.setErrorMessage('!Please fill all required fields')
      snackbarHook.open('!Please fill all required field', 'error')
      return false
    }

    if (!validateEmail(email)) {
      props.setErrorMessage('Email is invalid')
      snackbarHook.open('Email is invalid', 'error')

      return false
    }

    if (password !== confirmPassword) {
      props.setErrorMessage('!Password and Confirm Password should be the same')
      snackbarHook.open(
        '!Password and Confirm Password should be the same',
        'error',
      )

      return false
    }
    props.setErrorMessage('')
    return true
  }

  const submitData = (e: React.MouseEvent<{}>) => {
    e.preventDefault()

    if (!shouldEnableSubmit()) {
      return
    }

    setRegisterSubmitLoading(true)

    const name = firstName + ' ' + lastName

    const RegisterPromise = RegisterStudentRequest({
      name,
      email,
      schoolId: props.school_details._id,
      schoolNo: props.school_details.school_no,
      password,
      customAuthInput: userCustomInputAnswer,
    })

    RegisterPromise.then((response: any) => {
      const data = response.data
      if (data.data) {
        RegisterSuccessful(data.data)
        snackbarHook.open('Registration successful', 'success')
      }
    }).catch(err => {
      console.log('error registering students', err)
      setRegisterSubmitLoading(false)

      snackbarHook.open(err?.response?.data?.message, 'error')
      props.setErrorMessage(
        err.response.data
          ? err.response.data.message
          : 'Error Register In. Invalid User Credentials',
      )
    })
  }

  // RegisterSuccessful = (userData: any) => {
  //   alert("Registration successful. \n Check your mail to continue.");
  //   window.location.href = `http://${window.location.host}`;
  // };

  const RegisterSuccessful = (userData: any) => {
    schoolUserAuthHook.onAuthSuccessful(userData, () => {
      AnalyticService.onUserSignup({
        email: userData?.email,
        firstName: userData?.name,
        lastName: '',
        userId: userData?._id,
        userType: userData?.user_type,
      })

      trackFbPixelsRegistrationToSchoolDetails(props.school_details)

      if (props.onSuccess) {
        props.onSuccess()
      }

      if (!props.isDialog) {
        redirectOnAuthSuccess(redirectUrl)
      }
    })
  }

  const handleFirstNameChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setFirstName(e.currentTarget.value)
  }

  const handleLastNameChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setLastName(e.currentTarget.value)
  }

  const handleEmailChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setEmail(e.currentTarget.value)
  }

  const handlePasswordChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setPassword(e.currentTarget.value)
  }

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setConfirmPassword(e.currentTarget.value)
  }

  const handleCustomAuthFieldChange = (key: string, value: string) => {
    const user_custom_field: any = userCustomInputAnswer
    user_custom_field[key] = value
    setUserCustomInputAnswer(user_custom_field)
  }

  return (
    <div>
      {props.school_details && (
        <div style={{ marginBottom: '20px' }}>
          <TextField
            name={'name'}
            color="primary"
            fullWidth={true}
            variant="outlined"
            required={true}
            label="First Name"
            onChange={handleFirstNameChange}
            placeholder={"What's your name"}
            // helperText={"Name is required"}
            style={{ margin: '5px 0' }}
          />
          <TextField
            name={'name'}
            color="primary"
            fullWidth={true}
            variant="outlined"
            required={true}
            label="Last Name"
            onChange={handleLastNameChange}
            placeholder={"What's your name"}
            // helperText={"Name is required"}
            style={{ margin: '5px 0' }}
          />
          <TextField
            name={'email'}
            type={'email'}
            value={email}
            color="primary"
            required={true}
            fullWidth={true}
            variant="outlined"
            label="Email"
            onChange={handleEmailChange}
            placeholder={"What's your email"}
            helperText={
              email &&
              !validateEmail(email) && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Error
                    fontSize={'small'}
                    color={'error'}
                    style={{ marginRight: '5px' }}
                  />
                  <Typography variant={'inherit'} color={'error'}>
                    Email is invalid!
                  </Typography>
                </div>
              )
            }
            style={{ margin: '5px 0' }}
          />
          {customAuthInput?.map((item, index) => {
            const key = item.fieldName
            return (
              <React.Fragment key={index}>
                <TextField
                  name={key ? key.toLowerCase() : ''}
                  color="primary"
                  fullWidth={true}
                  required={item.isRequired}
                  // helperText={
                  //   item.isRequired
                  //     ? `${key} is required`
                  //     : `${key} is optional`
                  // }
                  variant="outlined"
                  label={key}
                  onChange={e => {
                    handleCustomAuthFieldChange(key, e.currentTarget.value)
                  }}
                  style={{ margin: '5px 0' }}
                />
              </React.Fragment>
            )
          })}

          <TextField
            name={'password'}
            color="primary"
            fullWidth={true}
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            required={true}
            placeholder={'****'}
            helperText={
              passwordNotSame && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Error
                    fontSize={'small'}
                    color={'error'}
                    style={{ marginRight: '5px' }}
                  />
                  <Typography variant={'inherit'} color={'error'}>
                    Password and Confirm Password should be the same
                  </Typography>
                </div>
              )
            }
            onChange={handlePasswordChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setShowPassword(!showPassword)
                    }}
                    size="large"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            style={{ margin: '5px 0' }}
          />
          <TextField
            name={'confirm-password'}
            color="primary"
            fullWidth={true}
            required={true}
            helperText={
              passwordNotSame && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Error
                    fontSize={'small'}
                    color={'error'}
                    style={{ marginRight: '5px' }}
                  />
                  <Typography variant={'inherit'} color={'error'}>
                    Password and Confirm Password should be the same
                  </Typography>
                </div>
              )
            }
            variant="outlined"
            placeholder={'****'}
            type={showPassword ? 'text' : 'password'}
            label="Confirm Password"
            onChange={handleConfirmPasswordChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setShowPassword(!showPassword)
                    }}
                    size="large"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            style={{ margin: '5px 0' }}
          />
          <Button
            color="primary"
            variant="contained"
            onClick={submitData}
            fullWidth={true}
            style={{ margin: '5px 0' }}
            disabled={registerSubmitLoading}
          >
            Register
            {registerSubmitLoading && (
              <CircularProgress size={15} style={{ marginLeft: '5px' }} />
            )}
          </Button>
          <AuthExtrafieldsDialogComponent
            openDialog={
              Boolean(registerWith) &&
              props.prefill?.userData?.continue_register_user
            }
            setOpenDialog={open => {
              !open && setRegisterWith('')
            }}
            schoolDetails={props.school_details}
            email={email}
            registerWith={registerWith}
            registerToken={props.prefill?.userData?.token}
            RegisterSuccessful={RegisterSuccessful}
            setLoading={setRegisterSubmitLoading}
            submitLoading={registerSubmitLoading}
          />
        </div>
      )}
    </div>
  )
}

export default withRouter(RegisterStudentComponent)
